import styled, { keyframes } from "styled-components";

/* ===== helpers ===== */
export const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));
export const usageColor = (pct: number) =>
  pct >= 90
    ? "#ef4444"
    : pct >= 80
    ? "#f97316"
    : pct >= 70
    ? "#eab308"
    : "#22c55e";
export const lighten = (hex: string, amount = 0.2) => {
  const c = hex.replace("#", "");
  const n = parseInt(c, 16);
  let r = (n >> 16) & 0xff,
    g = (n >> 8) & 0xff,
    b = n & 0xff;
  const t = (x: number) => Math.min(255, Math.floor(x + (255 - x) * amount));
  r = t(r);
  g = t(g);
  b = t(b);
  const h = (x: number) => x.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
};
export const formatStorage = (bytes: number, decimals = 1): string => {
  if (bytes === 0) return "0 B";
  const k = 1024,
    dm = decimals < 0 ? 0 : decimals;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const fadeUp = keyframes`0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-480px 0}100%{background-position:480px 0}`;

/* ===== Skeleton ===== */
export const Skel = styled.div`
  height: 16px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 480px 100%;
  animation: ${shimmer} 1.2s infinite;
`;

/* ===== SVM 카드 ===== */
export const SVMCard = styled.div`
  /* spacing */
  --pad-y: 14px;
  --pad-x: 16px;
  --gap-1: 6px;
  --gap-2: 8px;
  --gap-3: 10px;
  --cell-x: 12px;
  --cell-y: 10px;

  /* bars */
  --bar-h: clamp(12px, 1.8vh, 18px);
  --bar-min: 120px;
  --pct-minw: 44px;

  /* fluid type */
  --fz-title: clamp(14px, 2.2vw, 16px);
  --fz-count: clamp(10px, 1.5vw, 12px);
  --fz-base: clamp(12px, 1.6vw, 14px);
  --fz-sm: clamp(11px, 1.4vw, 13px);
  --fz-xs: clamp(10px, 1.2vw, 12px);
  --fz-th: clamp(11px, 1.5vw, 13px);
  --fz-td: var(--fz-base);
  --fz-usage: clamp(11px, 1.4vw, 13px);

  width: 100%;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.06);
  animation: ${fadeUp} 420ms ease both;

  /* 내부 스크롤 허용 */
  min-width: 0;
`;

export const SVMHead = styled.div<{ $pct: number }>`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: "title summary";
  column-gap: var(--gap-3);
  row-gap: var(--gap-2);
  align-items: center;

  padding: clamp(10px, 1.6vw, 18px) clamp(12px, 2vw, 22px);
  border-bottom: 1px dashed var(--border, #e2e8f0);
  background: ${({ $pct }) =>
    `linear-gradient(0deg, rgba(2,6,23,0), rgba(2,6,23,0)),
     linear-gradient(90deg, ${lighten("#22c55e", 0.82)}, transparent 40%)`};

  cursor: pointer;
  touch-action: manipulation;
  user-select: none;

  & > * {
    pointer-events: none;
  }
  & [data-click-pass] {
    pointer-events: auto;
  }

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "title"
      "summary";
  }
`;

export const SVMTitle = styled.div`
  grid-area: title;
  display: flex;
  gap: var(--gap-2);
  align-items: baseline;
  font-weight: 800;
  font-size: var(--fz-title);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const VolumnCount = styled.span`
  color: #64748b;
  font-weight: 600;
  font-size: var(--fz-count);
  @media (max-width: 420px) {
    display: none;
  }
`;

/* ===== Collapse ===== */
export const Collapse = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.28s ease;
  > div {
    overflow: hidden;
  }
`;
export const FadeBody = styled.div<{ $open: boolean }>`
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? 0 : -4)}px);
  transition: opacity 0.28s ease, transform 0.28s ease;
`;
export const Body = styled.div`
  display: grid;
  gap: var(--gap-3);
  padding: calc(var(--pad-y) + 2px) var(--pad-x) calc(var(--pad-y) + 4px);
  font-size: var(--fz-base);
  min-width: 0; /* ★ 중요 */
`;

/* ===== Sections ===== */
export const SubTitle = styled.div`
  font-weight: 800;
  color: var(--fg, #0f172a);
  margin: 2px 0 6px;
  font-size: var(--fz-base);
`;

/* ===== Network ===== */
export const NetGrid = styled.div`
  display: grid;
  gap: clamp(6px, 1.2vw, 10px);
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  }
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;
export const NetCard = styled.div`
  border: 1px dashed var(--border, #e2e8f0);
  border-radius: 12px;
  padding: calc(var(--pad-y) - 4px) calc(var(--pad-x) - 4px);
  display: grid;
  gap: var(--gap-1);
  background: #fbfdff;

  b {
    font-size: var(--fz-base);
  }
  small {
    color: var(--muted, #64748b);
    font-size: var(--fz-sm);
  }
  div {
    font-size: var(--fz-base);
  }
`;

/* ===== Volumes ===== */
export const TableWrap = styled.div`
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  background: var(--card, #fff);
  box-shadow: var(--shadow, 0 1px 2px rgba(2, 6, 23, 0.04));
  min-width: 0; /* ★ 중요 */
  overflow: hidden;

  .mobile-volumes {
    display: none;
  }

  @media (max-width: 680px) {
    .mobile-volumes {
      display: grid;
    }
    table,
    thead,
    tbody,
    tr,
    th,
    td {
      display: none;
    }
  }
`;

export const TableTop = styled.div`
  display: grid;
  gap: var(--gap-2);
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: calc(var(--pad-y) - 2px) calc(var(--pad-x) - 2px);
  border-bottom: 1px dashed var(--border, #e2e8f0);
  font-size: var(--fz-base);
  min-width: 0; /* ★ 중요 */
`;

export const Input = styled.input`
  height: 38px;
  padding: 0 12px 0 36px;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--card, #fff);
  color: var(--fg, #0f172a);
  border-radius: 10px;
  outline: none;
  box-shadow: var(--shadow, 0 1px 2px rgba(2, 6, 23, 0.04));
  min-width: 260px;
  transition: border 0.2s, box-shadow 0.2s;
  background-image: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='%2364748B' stroke-width='2' d='M21 21l-4.3-4.3M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px 50%;
  font-size: var(--fz-base);
  &:focus {
    border-color: var(--ring, #3b82f6);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15),
      var(--shadow, 0 1px 2px rgba(2, 6, 23, 0.04));
  }
  @media (max-width: 640px) {
    height: 34px;
    min-width: 200px;
  }
`;
export const Search = styled(Input)`
  min-width: auto;
  width: 100%;
  height: 34px;
`;

export const TableScroll = styled.div`
  width: 100%;
  min-width: 0; /* ★ 중요: 내부 스크롤 가능 */
  overflow-y: auto;
  max-height: clamp(260px, 40vh, 560px);

  /* 스크롤 공간 안정화(오버레이 스크롤바 환경에서도 레이아웃 흔들림 방지) */
  scrollbar-gutter: stable both-edges;

  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  /* 스크롤 트랙이 테두리에 살짝 가려지면 한 픽셀만 띄우기 */
  padding-bottom: 2px;

  @media (max-width: 680px) {
    max-height: none;
    overflow-y: visible;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fz-td);

  @media (max-width: 880px) {
    .col-agg {
      display: none;
    }
  }
`;

export const Th = styled.th`
  text-align: left;
  padding: var(--cell-y, 10px) var(--cell-x, 12px);
  border-bottom: 1px solid var(--border, #e2e8f0);
  background: #f9fbff;
  font-weight: 800;
  white-space: nowrap;
  font-size: var(--fz-th);
`;
export const Td = styled.td`
  padding: var(--cell-y, 10px) var(--cell-x, 12px);
  border-bottom: 1px dashed var(--border, #e2e8f0);
  vertical-align: middle;
  word-break: break-word;
  font-size: var(--fz-td);

  &:nth-child(3),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6) {
    padding-top: calc(var(--cell-y, 10px) - 2px);
    padding-bottom: calc(var(--cell-y, 10px) - 2px);
  }
`;

/* ===== Volumes: 모바일 카드 ===== */
export const MobileRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px dashed var(--border, #e2e8f0);
  background: #fff;
  &:nth-child(odd) {
    background: #fbfdff;
  }
  font-size: var(--fz-base);
`;
export const MobileCell = styled.div`
  display: grid;
  grid-template-columns: 92px 1fr;
  align-items: center;
  gap: 8px;

  label {
    color: #64748b;
    font-size: var(--fz-sm);
    white-space: nowrap;
  }
  b {
    font-size: var(--fz-base);
  }
  span {
    font-size: var(--fz-base);
  }

  &.maybe-hide {
    @media (max-width: 400px) {
      display: none;
    }
  }
`;

/* ===== Usage ===== */
export const UsageDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: var(--gap-2, 8px);
  margin-top: 4px;
`;
export const UsageBar = styled.div<{ $pct: number; $color: string }>`
  flex: 1;
  height: clamp(6px, 0.9vh, 10px);
  border-radius: 4px;
  background: #eef2f7;
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${({ $pct }) => clamp($pct, 0, 100)}%;
    background: ${({ $color }) =>
      `linear-gradient(90deg, ${lighten($color, 0.35)}, ${$color})`};
    transition: width 340ms ease;
    border-radius: 4px;
  }
`;
export const UsageText = styled.div<{ $color: string }>`
  font-weight: 800;
  font-size: var(--fz-usage);
  color: ${({ $color }) => $color};
  min-width: var(--pct-minw, 44px);
  text-align: right;
  white-space: nowrap;
`;

/* ===== Empty ===== */
export const Empty = styled.div`
  padding: calc(var(--pad-y, 14px) + 6px);
  color: var(--muted, #64748b);
  text-align: center;
  font-size: var(--fz-sm);
`;
/* === 하단에 추가 === */
export const VolumeActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
`;

export const ActionButton = styled.button<{ $type?: "resize" | "delete" }>`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid
    ${({ $type }) => ($type === "delete" ? "#ef4444" : "#3b82f6")};
  background: ${({ $type }) => ($type === "delete" ? "#fee2e2" : "#eff6ff")};
  color: ${({ $type }) => ($type === "delete" ? "#b91c1c" : "#1d4ed8")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $type }) => ($type === "delete" ? "#fecaca" : "#dbeafe")};
  }

  &:active {
    transform: scale(0.97);
  }
`;
export const TableTopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const TopActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

/* ===== Top Action Buttons ===== */
export const TopActionButton = styled.button<{
  $type?: "volume-create" | "project-create" | "project-resize";
}>`
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  ${({ $type }) => {
    switch ($type) {
      case "project-resize":
        return `
          border-color: #0ea5e9;
          background: #e0f2fe;
          color: #075985;
          &:hover { background: #bae6fd; }
        `;
      case "project-create":
        return `
          border-color: #6366f1;
          background: #eef2ff;
          color: #3730a3;
          &:hover { background: #e0e7ff; }
        `;
      default: /* volume-create */
        return `
          border-color: #22c55e;
          background: #dcfce7;
          color: #166534;
          &:hover { background: #bbf7d0; }
        `;
    }
  }}

  &:active {
    transform: scale(0.97);
  }
`;
